import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';

import { ReactComponent as AngleUpIcon } from 'assets/angle-up.svg';
import { ReactComponent as NewWindowIcon } from 'assets/new-window.svg';
import remixSpinner from 'assets/remix-spinner.gif';

import { getReviewDataAction, setDesignReviewHistory } from 'Redux/Actions/designReviewActions';
import { setCurrentRecord } from 'Redux/Actions/historyActions';

import { trackEvent } from 'services/eventTracking';
import {
  getDesignReviewPostProcess,
  refineDesignRemix,
  triggerDesignRemix,
} from 'services/teamDashboardReviews';

import { DESIGN_REMIX_POLL_INTERVAL, DESIGN_REMIX_STATUS } from 'utlis/constants';

import InspirationCarouselHeader from './InspirationCarouselHeader';
import RefinementInput from './RefinementInput';

const DEFAULT_REFINEMENT_PROMPT = 'Generate another variant of this design with the same insight but'
+ ' a different approach. A variant would help explore the insight further and spark new ideas.';

const formatDeeperInsightForRemix = insight => {
  if (!insight || typeof insight !== 'object') {
    return '';
  }

  const {
    detailed_feedback: detailedFeedback,
    high_impact_recommendation: highImpactRecommendation,
    action_steps: actionSteps,
    most_impacted_segment: mostImpactedSegment,
    primary_metrics_moved: primaryMetricsMoved,
    reason,
  } = insight;

  const lines = [];

  if (detailedFeedback) {
    lines.push(detailedFeedback);
  }

  const hasHighImpactSection = highImpactRecommendation
    || (Array.isArray(actionSteps) && actionSteps.length > 0);

  if (hasHighImpactSection) {
    if (lines.length) {
      lines.push('');
    }
    lines.push('High-impact recommendation');

    if (highImpactRecommendation) {
      lines.push(highImpactRecommendation);
    }
    if (Array.isArray(actionSteps) && actionSteps.length > 0) {
      actionSteps.forEach((step, index) => {
        lines.push(`${index + 1}. ${step}`);
      });
    }
  }

  if (mostImpactedSegment) {
    if (lines.length) {
      lines.push('');
    }
    lines.push('Most impacted segment');
    lines.push(mostImpactedSegment);
  }

  if (Array.isArray(primaryMetricsMoved) && primaryMetricsMoved.length > 0) {
    if (lines.length) {
      lines.push('');
    }
    lines.push('Primary metrics moved');
    lines.push(primaryMetricsMoved.join(', '));
  }

  if (reason) {
    if (lines.length) {
      lines.push('');
    }
    lines.push('Why');
    lines.push(reason);
  }

  return lines.join('\n');
};

export const DesignInspirationPanel = ({
  insight,
  insightUuid,
  currentFeedbackUuid,
  isPublicView = false,
  isCardExpanded = true,
}) => {
  const dispatch = useDispatch();

  const currentRecord = useSelector(state => state.historyReducer.currentRecord);
  const designReviewHistory = useSelector(state => state.designReview.designReviewHistory);
  const reviewCount = useSelector(state => state.designReview.reviewCount);
  const hasCredits = (reviewCount?.reviews_remaining ?? 0) > 0;
  const userData = useSelector(state => state.userStore);
  const userProfile = useSelector(state => state.userProfile);

  const [remixStatus, setRemixStatus] = useState(null);
  const [remixProcessUuid, setRemixProcessUuid] = useState(null);
  const [remixPreview, setRemixPreview] = useState(null);
  const [isRemixExpanded, setIsRemixExpanded] = useState(false);

  // Carousel & refinement state
  const [activeIndex, setActiveIndex] = useState(0);
  const [isRefining, setIsRefining] = useState(false);
  const [refiningProcessUuid, setRefiningProcessUuid] = useState(null);

  const hasResumedRef = useRef(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // When the feedback card is collapsed, collapse the inspiration image too
  useEffect(() => {
    if (!isCardExpanded) {
      setIsRemixExpanded(false);
    }
  }, [isCardExpanded]);

  const [remixError, setRemixError] = useState('');
  const [isTriggeringRemix, setIsTriggeringRemix] = useState(false);
  const [isFetchingImage, setIsFetchingImage] = useState(false);
  const [imageError, setImageError] = useState('');

  const isDeeperInsightFeedback = useMemo(
    () => Boolean(insight?.isDeeperInsight || insight?.category === 'deeper-insights'),
    [insight]
  );

  // Build unified inspirations array: original (index 0) + sorted refinements
  const inspirations = useMemo(() => {
    if (!remixPreview) {
      return [];
    }
    const original = { ...remixPreview, version: 1, isOriginal: true };
    const refinements = Array.isArray(remixPreview.refinements)
      ? remixPreview.refinements
        .filter(r => r.version >= 2)
        .sort((a, b) => a.version - b.version)
      : [];

    return [original, ...refinements];
  }, [remixPreview]);

  // Clamp activeIndex when inspirations array shrinks
  const safeIndex = Math.min(activeIndex, Math.max(inspirations.length - 1, 0));

  useEffect(() => {
    if (safeIndex !== activeIndex) {
      setActiveIndex(safeIndex);
    }
  }, [safeIndex, activeIndex]);

  // Derive active inspiration values using the safe (clamped) index
  const activeInspiration = inspirations[safeIndex] || null;
  const activeUploadKey = activeInspiration?.upload_info?.key;
  const activePreviewSrc = activeUploadKey
    ? `${process.env.REACT_APP_PREVIEW_CLOUDFRONT_URL}${activeUploadKey}`
    : null;
  const activeHtmlUploadKey = activeInspiration?.html_upload_info?.key;
  const activePreviewHtmlUrl = activeHtmlUploadKey
    ? `${process.env.REACT_APP_PREVIEW_CLOUDFRONT_URL}${activeHtmlUploadKey}`
    : null;
  const activeProcessUuid = activeInspiration?.process_uuid || null;
  const hasActiveImagePreview = Boolean(activePreviewSrc);

  const isRemixIdle = remixStatus === null || remixStatus === undefined;
  const isRemixInProgress = remixStatus === DESIGN_REMIX_STATUS.IN_PROGRESS;
  const isRemixComplete = remixStatus === DESIGN_REMIX_STATUS.COMPLETED;

  // Determine the status of the active carousel item
  const isActiveComplete = activeInspiration?.status === DESIGN_REMIX_STATUS.COMPLETED
    || (activeInspiration?.isOriginal && isRemixComplete);
  const isActiveInProgress = activeInspiration?.status === DESIGN_REMIX_STATUS.IN_PROGRESS
    || (activeInspiration?.isOriginal && isRemixInProgress);
  const isActiveFailed = activeInspiration?.status === DESIGN_REMIX_STATUS.FAILED
    || (activeInspiration?.isOriginal && remixStatus === DESIGN_REMIX_STATUS.FAILED);

  const activeRefinementPrompt = (!activeInspiration?.isOriginal && activeInspiration?.refinement_prompt)
    ? activeInspiration.refinement_prompt
    : null;

  // Carousel navigation
  const handlePrevInspiration = useCallback(() => {
    setActiveIndex(prev => Math.max(prev - 1, 0));
  }, []);

  const handleNextInspiration = useCallback(() => {
    const maxIndex = (isPublicView
      ? inspirations.filter(item => item.status === DESIGN_REMIX_STATUS.COMPLETED || item.isOriginal).length
      : inspirations.length) - 1;

    setActiveIndex(prev => Math.min(prev + 1, maxIndex));
  }, [inspirations, isPublicView]);

  const ensureDesignImageUrl = useCallback(async () => {
    const pageIndex = (insight?.page ?? 1) - 1;
    const framePath = currentRecord?.extra_data?.frame_images?.[pageIndex]
      || currentRecord?.extra_data?.frame_image;

    setIsFetchingImage(true);
    setImageError('');

    try {
      if (!framePath) {
        throw new Error('Missing design image path');
      }

      const prefixUrl = process.env.REACT_APP_FIREBASE_STORAGE_PREFIX;
      const postfixUrl = '?alt=media';
      const imageUrl = `${prefixUrl}${encodeURIComponent(framePath)}${postfixUrl}`;

      if (!imageUrl) {
        throw new Error('Design image unavailable');
      }

      return imageUrl;
    }
    catch (error) {
      trackEvent('[error] design inspiration image download failed', {
        insightUuid,
        recordUuid: currentRecord?.uuid,
        feedbackUuid: currentFeedbackUuid,
        message: error?.message,
      });

      setImageError('Unable to fetch design image URL');

      return null;
    }
    finally {
      setIsFetchingImage(false);
    }
  }, [currentRecord, insight, insightUuid, currentFeedbackUuid]);

  const updateRecordRemix = useCallback((payload = {}) => {
    if (!currentRecord || !insightUuid) {
      return;
    }

    const extraData = currentRecord.extra_data || {};
    const postProcesses = extraData.post_processes || {};
    const appliedPreview = postProcesses.applied_feedback_preview || {};
    const deeperInsights = postProcesses.deeper_insights || {};

    const existingAppliedEntry = appliedPreview[insightUuid] || {};
    const payloadRemixPreview = payload.remix_preview || {};

    const mergedAppliedEntry = {
      ...existingAppliedEntry,
      ...payload,
      remix_preview: {
        ...(existingAppliedEntry.remix_preview || {}),
        ...payloadRemixPreview,
        status: payloadRemixPreview.status
          ?? payload.status
          ?? existingAppliedEntry.remix_preview?.status,
        process_uuid: payloadRemixPreview.process_uuid
          ?? payload.process_uuid
          ?? existingAppliedEntry.remix_preview?.process_uuid,
        error_message: payloadRemixPreview.error_message
          ?? payload.error_message
          ?? existingAppliedEntry.remix_preview?.error_message,
        upload_info: payloadRemixPreview.upload_info
          ?? payload.upload_info
          ?? existingAppliedEntry.remix_preview?.upload_info,
        html_upload_info: payloadRemixPreview.html_upload_info
          ?? payload.html_upload_info
          ?? existingAppliedEntry.remix_preview?.html_upload_info,
        generated_at: payloadRemixPreview.generated_at
          ?? payload.generated_at
          ?? existingAppliedEntry.remix_preview?.generated_at,
      },
    };

    const updatedAppliedPreview = {
      ...appliedPreview,
      [insightUuid]: mergedAppliedEntry,
    };

    let updatedDeeperInsightsFeedback = deeperInsights.feedback;

    if (Array.isArray(deeperInsights.feedback)) {
      updatedDeeperInsightsFeedback = deeperInsights.feedback.map(insightItem => {
        if (String(insightItem?.uuid) !== String(insightUuid)) {
          return insightItem;
        }

        const existingRemixPreview = insightItem.remix_preview || {};
        const mergedRemixPreview = {
          ...existingRemixPreview,
          ...payloadRemixPreview,
        };

        const resolvedStatus = payloadRemixPreview.status
          ?? payload.status
          ?? existingRemixPreview.status;

        const resolvedProcessUuid = payloadRemixPreview.process_uuid
          ?? payload.process_uuid
          ?? existingRemixPreview.process_uuid;

        const resolvedPreviewBase64 = payloadRemixPreview.upload_info
          ?? payload.upload_info
          ?? existingRemixPreview.upload_info;

        const resolvedHtmlUpload = payloadRemixPreview.html_upload_info
          ?? payload.html_upload_info
          ?? existingRemixPreview.html_upload_info;

        const resolvedGeneratedAt = payloadRemixPreview.generated_at
          ?? payload.generated_at
          ?? existingRemixPreview.generated_at;

        const resolvedErrorMessage = payloadRemixPreview.error_message
          ?? payload.error_message
          ?? existingRemixPreview.error_message;

        return {
          ...insightItem,
          remix_preview: {
            ...mergedRemixPreview,
            status: resolvedStatus,
            process_uuid: resolvedProcessUuid,
            upload_info: resolvedPreviewBase64,
            html_upload_info: resolvedHtmlUpload,
            generated_at: resolvedGeneratedAt,
            error_message: resolvedErrorMessage,
          },
          upload_info: resolvedPreviewBase64 ?? insightItem.upload_info,
          html_upload_info: resolvedHtmlUpload ?? insightItem.html_upload_info,
        };
      });
    }

    const updatedRecord = {
      ...currentRecord,
      extra_data: {
        ...extraData,
        post_processes: {
          ...postProcesses,
          applied_feedback_preview: updatedAppliedPreview,
          deeper_insights: {
            ...deeperInsights,
            feedback: updatedDeeperInsightsFeedback,
          },
        },
      },
    };

    dispatch(setCurrentRecord(updatedRecord));

    if (Array.isArray(designReviewHistory?.results)) {
      const historyIndex = designReviewHistory.results.findIndex(
        record => String(record?.uuid) === String(currentRecord?.uuid)
      );

      if (historyIndex > -1) {
        const updatedHistory = {
          ...designReviewHistory,
          results: [...designReviewHistory.results],
        };

        updatedHistory.results[historyIndex] = {
          ...updatedHistory.results[historyIndex],
          ...updatedRecord,
        };

        dispatch(setDesignReviewHistory(updatedHistory));
      }
    }
  }, [currentRecord, dispatch, insightUuid, designReviewHistory]);

  // Sync remixPreview state from Redux store
  useEffect(() => {
    if (!isDeeperInsightFeedback || !insightUuid) {
      setRemixStatus(null);
      setRemixProcessUuid(null);
      setRemixPreview(null);
      setRemixError('');
      setIsRemixExpanded(false);

      return;
    }

    const previewBucket = currentRecord?.extra_data?.post_processes?.applied_feedback_preview || {};
    const existingEntry = previewBucket ? previewBucket[insightUuid] : null;

    let insightPreview = null;
    const deeperInsightsList = currentRecord?.extra_data?.post_processes?.deeper_insights?.feedback || [];

    if (Array.isArray(deeperInsightsList)) {
      const insightMatch = deeperInsightsList.find(item => String(item?.uuid) === String(insightUuid));

      if (insightMatch && insightMatch.remix_preview) {
        insightPreview = insightMatch.remix_preview;
      }
    }

    const finalPreview = insightPreview || existingEntry;

    if (finalPreview) {
      setRemixStatus(finalPreview.status ?? DESIGN_REMIX_STATUS.COMPLETED);
      setRemixProcessUuid(finalPreview.process_uuid || null);
      setRemixPreview(finalPreview);
      setRemixError(finalPreview.error_message || '');

      // Preserve current expanded state for completed previews — no state change needed
    }
    else {
      setRemixStatus(null);
      setRemixProcessUuid(null);
      setRemixPreview(null);
      setRemixError('');
      setIsRemixExpanded(false);
    }
  }, [currentRecord, insightUuid, isDeeperInsightFeedback]);

  // Original remix polling (unchanged)
  useEffect(() => {
    if (
      !isDeeperInsightFeedback
      || !remixProcessUuid
      || remixStatus !== DESIGN_REMIX_STATUS.IN_PROGRESS
      || isPublicView
    ) {
      return undefined;
    }

    let cancelled = false;
    let intervalId = null;

    const stopPolling = () => {
      cancelled = true;

      if (intervalId) {
        clearInterval(intervalId);
      }
    };

    const checkStatus = async () => {
      try {
        const postProcess = await getDesignReviewPostProcess(remixProcessUuid);

        if (!postProcess || cancelled) {
          return;
        }

        if (postProcess.status !== undefined) {
          setRemixStatus(postProcess.status);
        }

        if (postProcess.status === DESIGN_REMIX_STATUS.COMPLETED) {
          const resultData = postProcess.result_data || {};

          setRemixPreview(resultData);
          setRemixError('');
          setIsRemixExpanded(true);

          updateRecordRemix({
            status: DESIGN_REMIX_STATUS.COMPLETED,
            process_uuid: postProcess.uuid,
            upload_info: resultData.upload_info,
            html_upload_info: resultData.html_upload_info,
            generated_at: resultData.generated_at,
            remix_preview: {
              status: DESIGN_REMIX_STATUS.COMPLETED,
              upload_info: resultData.upload_info,
              html_upload_info: resultData.html_upload_info,
              generated_at: resultData.generated_at,
              process_uuid: postProcess.uuid,
            },
          });

          dispatch(getReviewDataAction());
          stopPolling();
        }
        else if (postProcess.status === DESIGN_REMIX_STATUS.FAILED) {
          const errorMessage = postProcess.error_message || 'Design remix failed';

          setRemixError(errorMessage);

          updateRecordRemix({
            status: DESIGN_REMIX_STATUS.FAILED,
            process_uuid: postProcess.uuid,
            error_message: errorMessage,
            remix_preview: {
              status: DESIGN_REMIX_STATUS.FAILED,
              error_message: errorMessage,
              process_uuid: postProcess.uuid,
            },
          });

          stopPolling();
        }
      }
      catch (error) {
        if (!cancelled) {
          setRemixError(error?.response?.data?.message || 'Unable to fetch remix status');
        }
      }
    };

    checkStatus();

    intervalId = setInterval(checkStatus, DESIGN_REMIX_POLL_INTERVAL);

    return () => {
      stopPolling();
    };
  }, [isDeeperInsightFeedback, remixProcessUuid, remixStatus, updateRecordRemix, isPublicView, dispatch]);

  // Refinement polling (separate from original)
  useEffect(() => {
    if (!refiningProcessUuid || isPublicView) {
      return undefined;
    }

    let cancelled = false;
    let intervalId = null;

    const stopPolling = () => {
      cancelled = true;

      if (intervalId) {
        clearInterval(intervalId);
      }
    };

    const checkRefinementStatus = async () => {
      try {
        const postProcess = await getDesignReviewPostProcess(refiningProcessUuid);

        if (!postProcess || cancelled) {
          return;
        }

        if (postProcess.status === DESIGN_REMIX_STATUS.COMPLETED) {
          const resultData = postProcess.result_data || {};

          // Compute updated refinements outside setter so we can persist to Redux
          let updatedRefinementsSnapshot = [];

          setRemixPreview(prev => {
            if (!prev) {
              return prev;
            }

            updatedRefinementsSnapshot = Array.isArray(prev.refinements)
              ? prev.refinements.map(r => {
                if (r.process_uuid === refiningProcessUuid) {
                  return {
                    ...r,
                    status: DESIGN_REMIX_STATUS.COMPLETED,
                    upload_info: resultData.upload_info,
                    html_upload_info: resultData.html_upload_info,
                    generated_at: resultData.generated_at,
                  };
                }

                return r;
              })
              : [];

            return { ...prev, refinements: updatedRefinementsSnapshot };
          });

          if (!cancelled) {
            setIsRemixExpanded(true);
            setRefiningProcessUuid(null);
            setIsRefining(false);

            // Persist refinement completion + full refinements array to Redux store
            updateRecordRemix({
              remix_preview: {
                refinements: updatedRefinementsSnapshot,
              },
            });

            dispatch(getReviewDataAction());
          }

          stopPolling();
        }
        else if (postProcess.status === DESIGN_REMIX_STATUS.FAILED) {
          const errorMessage = postProcess.error_message || 'Refinement failed';

          let failedRefinementsSnapshot = [];

          setRemixPreview(prev => {
            if (!prev) {
              return prev;
            }

            failedRefinementsSnapshot = Array.isArray(prev.refinements)
              ? prev.refinements.map(r => {
                if (r.process_uuid === refiningProcessUuid) {
                  return {
                    ...r,
                    status: DESIGN_REMIX_STATUS.FAILED,
                    error_message: errorMessage,
                  };
                }

                return r;
              })
              : [];

            return { ...prev, refinements: failedRefinementsSnapshot };
          });

          if (!cancelled) {
            setRemixError(errorMessage);
            setRefiningProcessUuid(null);
            setIsRefining(false);

            // Persist refinement failure + full refinements array to Redux store
            updateRecordRemix({
              remix_preview: {
                refinements: failedRefinementsSnapshot,
              },
            });
          }

          stopPolling();
        }
      }
      catch (error) {
        if (!cancelled) {
          setRemixError(error?.response?.data?.message || 'Unable to fetch refinement status');
        }
      }
    };

    checkRefinementStatus();
    intervalId = setInterval(checkRefinementStatus, DESIGN_REMIX_POLL_INTERVAL);

    return () => {
      stopPolling();
    };
  }, [refiningProcessUuid, isPublicView, updateRecordRemix, dispatch]);

  // Resume refinement polling on mount if there's an in-progress refinement
  useEffect(() => {
    if (hasResumedRef.current || !remixPreview || isPublicView) {
      return;
    }

    const refinements = Array.isArray(remixPreview.refinements) ? remixPreview.refinements : [];
    const inProgressRefinement = refinements.find(
      r => r.status === DESIGN_REMIX_STATUS.IN_PROGRESS && r.process_uuid
    );

    if (inProgressRefinement) {
      hasResumedRef.current = true;
      setRefiningProcessUuid(inProgressRefinement.process_uuid);
      setIsRefining(true);

      // Navigate to the in-progress item
      const idx = refinements
        .filter(r => r.version >= 2)
        .sort((a, b) => a.version - b.version)
        .findIndex(r => r.process_uuid === inProgressRefinement.process_uuid);

      if (idx >= 0) {
        setActiveIndex(idx + 1); // +1 because original is at index 0
      }
    }
    else {
      hasResumedRef.current = true;
    }
  }, [remixPreview, isPublicView]);

  const handleGenerateRemix = useCallback(async () => {
    if (!hasCredits) {
      setRemixError('You have no credits remaining. Please upgrade your plan.');

      return;
    }

    const figmaUserId = userData?.figma_user_id || userProfile?.figma_user_id;

    if (!isDeeperInsightFeedback || !insightUuid || isTriggeringRemix || !figmaUserId) {
      return;
    }

    setIsTriggeringRemix(true);
    setRemixError('');
    setImageError('');

    try {
      const imageUrlForRemix = await ensureDesignImageUrl();

      if (!imageUrlForRemix) {
        throw new Error('Design image unavailable');
      }

      const response = await triggerDesignRemix({
        insightUuid,
        imageUrl: imageUrlForRemix,
        deeperInsightText: formatDeeperInsightForRemix(insight ?? {}),
        figmaUserId,
        designReviewUuid: currentFeedbackUuid,
      });

      setRemixProcessUuid(response.process_uuid);
      setRemixStatus(response.status ?? DESIGN_REMIX_STATUS.IN_PROGRESS);
      setRemixPreview(null);
      setIsRemixExpanded(false);

      updateRecordRemix({
        status: response.status ?? DESIGN_REMIX_STATUS.IN_PROGRESS,
        process_uuid: response.process_uuid,
        upload_info: null,
        html_upload_info: null,
        generated_at: null,
        remix_preview: {
          status: DESIGN_REMIX_STATUS.IN_PROGRESS,
          process_uuid: response.process_uuid,
          html_upload_info: null,
        },
      });
    }
    catch (error) {
      const remixErrorMsg = error?.response?.data?.message
        || error?.message || 'Failed to start design remix. Please try again.';

      setRemixError(remixErrorMsg);
    }
    finally {
      setIsTriggeringRemix(false);
    }
  }, [
    currentFeedbackUuid,
    ensureDesignImageUrl,
    hasCredits,
    insight,
    insightUuid,
    isDeeperInsightFeedback,
    isTriggeringRemix,
    updateRecordRemix,
    userData,
    userProfile,
  ]);

  const handleRefineRemix = useCallback(async refinementPrompt => {
    if (isRefining || !activeInspiration || !insightUuid) {
      return;
    }

    if (!hasCredits) {
      setRemixError('You have no credits remaining. Please upgrade your plan.');

      return;
    }

    const figmaUserId = userData?.figma_user_id || userProfile?.figma_user_id;

    if (!figmaUserId) {
      setRemixError('Figma account not connected. Please connect your Figma account.');

      return;
    }

    const effectivePrompt = refinementPrompt?.trim() || DEFAULT_REFINEMENT_PROMPT;

    setIsRefining(true);
    setRemixError('');
    setImageError('');

    try {
      const imageUrlForRemix = await ensureDesignImageUrl();

      if (!isMountedRef.current) {
        return;
      }

      if (!imageUrlForRemix) {
        throw new Error('Design image unavailable');
      }

      const htmlUploadKey = activeInspiration.html_upload_info?.key;
      const htmlUrl = htmlUploadKey
        ? `${process.env.REACT_APP_PREVIEW_CLOUDFRONT_URL}${htmlUploadKey}`
        : null;
      const maxVersion = inspirations.reduce(
        (max, item) => Math.max(max, item.version || 1),
        1
      );
      const nextVersion = maxVersion + 1;

      const response = await refineDesignRemix({
        designReviewUuid: currentFeedbackUuid,
        insightUuid,
        refinementPrompt: effectivePrompt,
        htmlUrl,
        imageUrl: imageUrlForRemix,
        parentProcessUuid: activeProcessUuid,
        version: nextVersion,
        insightText: formatDeeperInsightForRemix(insight ?? {}),
        figmaUserId,
      });

      if (!isMountedRef.current) {
        return;
      }

      // Append in-progress refinement to remixPreview.refinements
      const newRefinement = {
        version: nextVersion,
        status: response.status ?? DESIGN_REMIX_STATUS.IN_PROGRESS,
        process_uuid: response.process_uuid,
        refinement_prompt: effectivePrompt,
        upload_info: null,
        html_upload_info: null,
      };

      setRemixPreview(prev => {
        if (!prev) {
          return prev;
        }
        const existing = Array.isArray(prev.refinements) ? prev.refinements : [];

        return { ...prev, refinements: [...existing, newRefinement] };
      });

      setRefiningProcessUuid(response.process_uuid);

      // Navigate to the new last item using functional updater to avoid stale closure
      setActiveIndex(() => {
        const existingCount = Array.isArray(remixPreview?.refinements)
          ? remixPreview.refinements.filter(r => r.version >= 2).length
          : 0;

        return existingCount + 1; // original(0) + existing refinements + new item
      });
    }
    catch (error) {
      if (!isMountedRef.current) {
        return;
      }

      const msg = error?.response?.data?.message
        || error?.message || 'Failed to start refinement. Please try again.';

      setRemixError(msg);
      setIsRefining(false);
    }
  }, [
    activeInspiration,
    activeProcessUuid,
    currentFeedbackUuid,
    ensureDesignImageUrl,
    hasCredits,
    insight,
    inspirations,
    insightUuid,
    isRefining,
    remixPreview,
    userData,
    userProfile,
  ]);

  const handleOpenHtmlPreview = useCallback(() => {
    if (!activePreviewHtmlUrl) {
      return;
    }

    const baseURL = `${process.env.REACT_APP_DESIGN_PRO_BASE_URL || window.location.origin}preview-share/`;
    const finalURL = baseURL + activeProcessUuid;

    window.open(finalURL, '_blank', 'noopener,noreferrer');
  }, [activePreviewHtmlUrl, activeProcessUuid]);

  if (!isDeeperInsightFeedback) {
    return null;
  }

  // In public view, filter out in-progress refinements for carousel count
  const publicInspirations = isPublicView
    ? inspirations.filter(item => item.status === DESIGN_REMIX_STATUS.COMPLETED || item.isOriginal)
    : inspirations;

  // In public view, only show the panel if there's a completed preview
  if (isPublicView && publicInspirations.length === 0) {
    return null;
  }
  if (
    isPublicView && (isRemixIdle || isRemixInProgress)
    && !hasActiveImagePreview && !activePreviewHtmlUrl
  ) {
    return null;
  }

  const showCarousel = inspirations.length > 0 && !isRemixIdle;

  return (
    <div className="flex flex-col gap-8">
      {/* Idle state: no inspirations generated yet */}
      {isRemixIdle && !isPublicView && (
        <div className="flex justify-between items-center gap-2">
          <div className="flex flex-col gap-0">
            <p className="font-medium text-[11px] text-black/50 leading-4">
              Inspiration
            </p>
            <p className="font-normal text-[14px] leading-5 text-black mt-0.5">
              Turn this idea into a prototype
            </p>
          </div>
          <button
            type="button"
            className="flex items-center justify-center shrink-0 w-[78px] h-8 py-2 px-3
              bg-primary-500 rounded-lg text-white text-xs font-medium leading-4
              hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            onClick={handleGenerateRemix}
            disabled={!insightUuid || isTriggeringRemix || isFetchingImage || !hasCredits}
          >
            {isTriggeringRemix || isFetchingImage ? 'Preparing…' : 'Generate'}
          </button>
        </div>
      )}

      {/* Carousel header: shown once we have at least one inspiration */}
      {showCarousel && (
        <InspirationCarouselHeader
          activeIndex={activeIndex}
          totalCount={isPublicView ? publicInspirations.length : inspirations.length}
          onPrev={handlePrevInspiration}
          onNext={handleNextInspiration}
        >
          {isActiveComplete && !hasActiveImagePreview && activePreviewHtmlUrl && (
            <div className="flex gap-2">
              <button
                type="button"
                className="bg-transparent border border-black/20 text-gray-900/90 text-[11px]
                  font-medium leading-4 tracking-[0.055px] rounded-md px-3 py-2 cursor-pointer
                  hover:bg-black/[0.04]"
                onClick={handleOpenHtmlPreview}
              >
                Open prototype
              </button>
            </div>
          )}
        </InspirationCarouselHeader>
      )}

      {/* In-progress spinner (original or refinement) */}
      {isActiveInProgress && !isPublicView && (
        <div className="flex justify-center items-center rounded-xl border border-[#E6E6E6] bg-white select-none py-60">
          <div className="flex flex-col justify-center items-center gap-24">
            <img src={remixSpinner} alt="Remix in progress" className="w-[34px] h-[34px]" />
            <div className="font-normal text-[12px] leading-4 text-center text-black/50">
              Generating inspiration in up to 5 minutes
            </div>
          </div>
        </div>
      )}

      {/* Completed preview image */}
      {isActiveComplete && hasActiveImagePreview && (
        <div className="relative mt-1">
          <div
            className="overflow-hidden rounded-xl bg-[#F8F9FB]"
          >
            <div
              className={cn(
                'relative w-full overflow-hidden bg-white transition-all duration-300 border border-black/16'
                + ' rounded-xl',
                {
                  'max-h-[560px]': isRemixExpanded,
                  'max-h-[220px]': !isRemixExpanded,
                }
              )}
            >
              <div
                className="absolute top-0 left-0 right-0 w-full flex items-center justify-between p-2 z-10"
              >
                <button
                  type="button"
                  className="flex flex-row items-center justify-center p-1 rounded-md
                    border border-[#E1E4E8] bg-white text-black/90 cursor-pointer"
                  onClick={() => setIsRemixExpanded(prev => !prev)}
                  aria-expanded={isRemixExpanded}
                  aria-label={isRemixExpanded ? 'Collapse preview' : 'Expand preview'}
                >
                  <div className="flex justify-center items-center w-4 h-4">
                    <AngleUpIcon
                      className={cn('transition-transform duration-200', {
                        'rotate-180': !isRemixExpanded,
                      })}
                    />
                  </div>
                </button>
                <button
                  type="button"
                  className="flex flex-row items-center justify-center p-1 rounded-md
                    border border-[#E1E4E8] bg-white text-black/90 cursor-pointer
                    disabled:opacity-45 disabled:cursor-not-allowed"
                  onClick={handleOpenHtmlPreview}
                  disabled={!activePreviewHtmlUrl}
                >
                  <div className="flex justify-center items-center w-4 h-4">
                    <NewWindowIcon className="transition-transform duration-200" />
                  </div>
                </button>
              </div>
              {!isPublicView && (
                <div
                  className="absolute left-0 right-0 bottom-0 p-2 rounded-b-xl bg-gradient-to-t
                  from-black/40 to-transparent z-10"
                >
                  <RefinementInput
                    onSubmit={handleRefineRemix}
                    isSubmitting={isRefining}
                    disabled={!activePreviewHtmlUrl || !hasCredits}
                  />
                </div>
              )}
              <img
                src={activePreviewSrc}
                alt="Design remix preview"
                className="w-full block object-cover"
              />
            </div>
            {activeRefinementPrompt && (
              <div className="bg-[#F8F9FB] px-[16px] py-[12px]">
                <p className="font-normal text-sm leading-5 text-black m-0">
                  "
                  {activeRefinementPrompt}
                  "
                </p>
              </div>
            )}
            {!hasCredits && !isPublicView && (
              <>
                <hr />
                <p className="px-[16px] py-[12px] font-normal text-sm text-black leading-5 m-0">
                  Usage limit reached &bull; Resets monthly
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {/* Failed state */}
      {isActiveFailed && !isPublicView && (
        <>
          <p className="m-0 text-xs text-[#DC3412]">
            {remixError || 'Remix failed. Please try again.'}
          </p>
          {activeInspiration?.isOriginal
            ? (
              <button
                type="button"
                className="flex justify-between items-center gap-2 px-2 py-2 rounded-md border border-black/20
                bg-white text-gray-800 hover:shadow-[0px_4px_8px_rgba(96,97,112,0.16),0px_0px_2px_rgba(40,41,61,0.04)]
                transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleGenerateRemix}
                disabled={isTriggeringRemix || isFetchingImage}
              >
                Try again
              </button>
            )
            : (
              <RefinementInput
                onSubmit={handleRefineRemix}
                isSubmitting={isRefining}
                disabled={!hasCredits}
              />
            )}
        </>
      )}

      {remixError && !isActiveFailed && !isActiveInProgress && (
        <p className="m-0 text-xs text-[#DC3412]">
          {remixError}
        </p>
      )}

      {imageError && (
        <p className="m-0 text-xs text-[#DC3412]">
          {imageError}
        </p>
      )}

      {!insightUuid && (
        <p className="m-0 text-[11px] text-gray-900/55">
          Prototype unavailable for this new idea.
        </p>
      )}
    </div>
  );
};

DesignInspirationPanel.propTypes = {
  insight: PropTypes.object.isRequired,
  insightUuid: PropTypes.string,
  currentFeedbackUuid: PropTypes.string,
  isPublicView: PropTypes.bool,
  isCardExpanded: PropTypes.bool,
};

DesignInspirationPanel.defaultProps = {
  insightUuid: undefined,
  currentFeedbackUuid: undefined,
  isPublicView: false,
  isCardExpanded: true,
};

export default DesignInspirationPanel;
